import React, { useCallback, useMemo, useState } from "react";
import { Popover, Tooltip } from "antd";
import { isActionColumn, siteVisitColumnMeta } from "./siteVisitColumnOrder";

/**
 * Excel-style column sizing for the site-visit tables: drag a header edge to
 * widen or narrow a column, and fold a column you don't need right now down to
 * a sliver instead of turning it off entirely.
 *
 * Why both: these tables are 20–40 columns of one site visit card. The
 * "Key fields / All fields" toggle (useSiteVisitColumns) answers "which columns
 * do I want at all", but people also want the *shape* of what's on screen — the
 * long free-text columns (instructions, notes) squeezed down so the dates line
 * up on one screen, and the one column they're working in made wide enough to
 * read. That is per-person and per-table, so it lives in localStorage rather
 * than in anyone's defaults.
 *
 * Design notes:
 *   * A column is left completely alone until the user sizes or folds it — no
 *     wrapper element, no width, exactly the markup the table declared. So the
 *     tables that carefully size themselves (the Site Visit Summary runs
 *     `table-layout: auto` so columns fit their widest value) keep doing that
 *     until someone deliberately overrides one column.
 *   * A sized column's width is pinned on the cells themselves — `width`,
 *     `min-width` and `max-width` on every `<th>`/`<td>` of that column, as a
 *     border-box — because with `table-layout: auto` a `<col>` width is only a
 *     hint. The number you dragged to is therefore the number the column ends
 *     up, padding included, so dragging the same edge twice starts where it
 *     left off instead of creeping wider each time.
 *   * Header and body content both clip inside that box (hidden overflow,
 *     ellipsis, no wrapping), which is what stops a long label from running
 *     over its neighbour and is also what lets the column shrink at all: a
 *     clipping box has no minimum content width, so the pinned width wins.
 *   * Dragging previews the new width through an injected stylesheet and only
 *     commits to React state on mouse-up, so a 40-column table isn't
 *     re-rendered per frame and nothing React owns is written to behind its
 *     back (see `startResize`).
 *   * The row-action column is never sizeable — it is a fixed strip of icons —
 *     but it does fold. It is pinned to the right on the wide tables, so it
 *     costs the scrollable area its width on every row whether or not anyone is
 *     acting on a visit right now; folded, the same icons move into a popover
 *     behind one button and the table gets ~90px back.
 */

export const MIN_COLUMN_WIDTH = 56;
export const FOLDED_COLUMN_WIDTH = 34;
// Wider than a folded data column: it still has to hold a button you can hit.
export const FOLDED_ACTION_WIDTH = 46;

// Marks the cells and <col>s of the column being dragged. An attribute rather
// than a class because React owns `className` on all of them and would drop it
// on any re-render mid-drag; it never touches attributes it did not set.
// siteVisitColumns.scss clips against it, and the drag writes the live width
// into a stylesheet keyed on it.
const DRAG_ATTR = "data-sv-resizing";

const widthsKey = (tableId) => `siteVisitColumns.${tableId}.widths`;
const foldedKey = (tableId) => `siteVisitColumns.${tableId}.folded`;

const readStore = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A blocked localStorage only costs the remembered layout.
  }
};

/** The key a column is remembered under; falls back to its dataIndex. */
const columnId = (column) => {
  const key = column?.key ?? column?.dataIndex;
  return Array.isArray(key) ? key.join(".") : key;
};

/** Plain-text label for tooltips: the title is often a node, not a string. */
const columnLabel = (column) => {
  if (typeof column?.title === "string") return column.title;
  const meta = siteVisitColumnMeta(columnId(column));
  return meta?.label || String(columnId(column) ?? "Column");
};

export const useColumnSizing = ({ tableId, columns }) => {
  const [widths, setWidths] = useState(() => readStore(widthsKey(tableId), {}));
  const [folded, setFolded] = useState(() => readStore(foldedKey(tableId), []));

  const setWidth = useCallback(
    (id, width) => {
      setWidths((prev) => {
        const next = { ...prev, [id]: width };
        writeStore(widthsKey(tableId), next);
        return next;
      });
    },
    [tableId]
  );

  const clearWidth = useCallback(
    (id) => {
      setWidths((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        writeStore(widthsKey(tableId), next);
        return next;
      });
    },
    [tableId]
  );

  const toggleFold = useCallback(
    (id) => {
      setFolded((prev) => {
        const next = prev.includes(id)
          ? prev.filter((key) => key !== id)
          : [...prev, id];
        writeStore(foldedKey(tableId), next);
        return next;
      });
    },
    [tableId]
  );

  const reset = useCallback(() => {
    setWidths({});
    setFolded([]);
    writeStore(widthsKey(tableId), {});
    writeStore(foldedKey(tableId), []);
  }, [tableId]);

  const decorateActionColumn = useCallback(
    (column, id) => {
      const isFolded = folded.includes(id);
      const next = {
        ...column,
        onHeaderCell: (headerColumn) => {
          const base = column.onHeaderCell
            ? column.onHeaderCell(headerColumn)
            : {};
          return {
            ...base,
            className: `${base.className || ""} svHead--actions`.trim(),
            sizing: {
              id,
              label: "Actions",
              folded: isFolded,
              width: isFolded ? FOLDED_ACTION_WIDTH : undefined,
              resizable: false,
              onFold: () => toggleFold(id),
            },
          };
        },
      };

      if (!isFolded) return next;

      next.width = FOLDED_ACTION_WIDTH;
      next.onCell = (record, index) => {
        const base = column.onCell ? column.onCell(record, index) : {};
        return {
          ...base,
          className: `${base.className || ""} svCellHost`.trim(),
          style: {
            ...base.style,
            width: FOLDED_ACTION_WIDTH,
            minWidth: FOLDED_ACTION_WIDTH,
            maxWidth: FOLDED_ACTION_WIDTH,
          },
        };
      };

      // The column's own render is reused verbatim inside the popover, so the
      // icons, their tooltips and their permission checks are the same ones —
      // there is no second copy of the action strip to keep in step.
      const render = column.render;
      next.render = (value, record, index) => (
        <FoldedActions>
          {render ? render(value, record, index) : value}
        </FoldedActions>
      );
      return next;
    },
    [folded, toggleFold]
  );

  const decorate = useCallback(
    (column) => {
      // Column groups (the Site Visit Summary's phase bands) carry their real
      // columns underneath; the band header itself isn't sizeable.
      if (Array.isArray(column.children)) {
        return { ...column, children: column.children.map(decorate) };
      }

      const id = columnId(column);
      if (!id) return column;

      // The row-action column folds but never resizes: its width is whatever the
      // strip of icons needs, so the only two useful states are "icons on the
      // row" and "icons one click away".
      if (isActionColumn(column)) return decorateActionColumn(column, id);

      const isFolded = folded.includes(id);
      const sizedWidth = isFolded
        ? FOLDED_COLUMN_WIDTH
        : widths[id] ?? undefined;
      const label = columnLabel(column);

      const next = {
        ...column,
        onHeaderCell: (headerColumn) => ({
          // Anything the column already asked for (Implementation.scss pins a
          // class on its phase band cells, for instance) has to survive.
          ...(column.onHeaderCell ? column.onHeaderCell(headerColumn) : {}),
          sizing: {
            id,
            label,
            folded: isFolded,
            width: sizedWidth,
            onFold: () => toggleFold(id),
            onResize: (width) => setWidth(id, width),
            onResetWidth: () => clearWidth(id),
          },
        }),
      };

      // Untouched column: hand back the original, so nothing about how it
      // renders or measures changes.
      if (sizedWidth == null) return next;

      next.width = sizedWidth;

      // The body cells carry the same pinned width as the header. Without this
      // the column is only as narrow as its widest value allows, so a narrowed
      // column stays wide and its header label runs over the next column.
      next.onCell = (record, index) => {
        const base = column.onCell ? column.onCell(record, index) : {};
        return {
          ...base,
          className: `${base.className || ""} svCellHost`.trim(),
          style: {
            ...base.style,
            width: sizedWidth,
            minWidth: sizedWidth,
            maxWidth: sizedWidth,
          },
        };
      };

      const render = column.render;
      next.render = isFolded
        ? () => <span className="svCell__folded" aria-hidden="true" />
        : (value, record, index) => (
            <div className="svCell">
              {render ? render(value, record, index) : value}
            </div>
          );
      return next;
    },
    [folded, widths, toggleFold, setWidth, clearWidth, decorateActionColumn]
  );

  const decorated = useMemo(
    () => (columns || []).filter(Boolean).map(decorate),
    [columns, decorate]
  );

  const components = useMemo(
    () => ({ header: { cell: SizingHeaderCell } }),
    []
  );

  const touched = folded.length > 0 || Object.keys(widths).length > 0;
  const sizingBar = touched ? (
    <button type="button" className="svSizingReset" onClick={reset}>
      Reset column layout
    </button>
  ) : null;

  return { columns: decorated, components, sizingBar, resetSizing: reset };
};

/**
 * A folded row-action cell: one button that opens the row's own action strip.
 *
 * The strip is the column's real render output, so nothing is reimplemented here
 * — the icons keep their tooltips and whatever role checks put them there.
 */
const FoldedActions = ({ children }) => (
  <Popover
    trigger="click"
    placement="leftTop"
    overlayClassName="svActionPopover"
    content={<div className="svActionPopover__body">{children}</div>}
  >
    <button type="button" className="svActionTrigger" aria-label="Row actions">
      <svg viewBox="0 0 16 4" width="14" height="4" aria-hidden="true">
        <circle cx="2" cy="2" r="1.6" fill="currentColor" />
        <circle cx="8" cy="2" r="1.6" fill="currentColor" />
        <circle cx="14" cy="2" r="1.6" fill="currentColor" />
      </svg>
    </button>
  </Popover>
);

/**
 * Where a `<th>` sits among the table's leaf columns.
 *
 * Needed to move the whole column (not just its header) while a drag is in
 * flight: antd renders one `<col>` per leaf column, and the body cells follow
 * whichever `<col>` they sit under. A single-row header could use `cellIndex`,
 * but the Site Visit Summary's header is two rows — identity columns spanning
 * both, phase bands above their children — so this walks the header the way the
 * HTML table model does, honouring rowSpan/colSpan.
 */
const leafColumnIndex = (th) => {
  const head = th.closest("thead");
  if (!head) return -1;

  const taken = [];
  let index = -1;

  Array.from(head.rows).forEach((row, r) => {
    let column = 0;
    Array.from(row.cells).forEach((cell) => {
      while (taken[r]?.[column]) column += 1;
      const colSpan = cell.colSpan || 1;
      const rowSpan = cell.rowSpan || 1;
      if (cell === th) index = column;
      for (let dr = 0; dr < rowSpan; dr += 1) {
        taken[r + dr] = taken[r + dr] || [];
        for (let dc = 0; dc < colSpan; dc += 1) {
          taken[r + dr][column + dc] = true;
        }
      }
      column += colSpan;
    });
  });

  return index;
};

/**
 * Header cell with a drag handle on its right edge and a fold toggle.
 *
 * Dragging is modelled on a spreadsheet: a dashed guide line runs the full
 * height of the table at the cursor, a badge reads out the column's new width
 * and how far it has moved, and the column itself follows live. All of that is
 * plain DOM for the duration of the drag — the width is committed to React
 * state once, on release, so a 40-column table isn't re-rendered per frame.
 * Double-clicking the handle returns the column to its natural width.
 */
const SizingHeaderCell = ({ sizing, children, className = "", ...rest }) => {
  if (!sizing) {
    return (
      <th {...rest} className={className}>
        {children}
      </th>
    );
  }

  const {
    label,
    folded,
    width,
    onFold,
    onResize,
    onResetWidth,
    resizable = true,
  } = sizing;

  const startResize = (event) => {
    // Left button only, and never let the click reach a sorter.
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();

    const cell = event.currentTarget.closest("th");
    if (!cell) return;

    const startX = event.clientX;
    const startWidth = cell.getBoundingClientRect().width;
    let nextWidth = startWidth;

    // The whole column follows the drag: antd keeps one <col> per leaf column
    // (in each of its tables, when the header and body are split), and the body
    // cells size from it.
    const root = cell.closest(".ant-table") || cell.closest("table");
    const index = leafColumnIndex(cell);
    const cols =
      index < 0 || !root
        ? []
        : Array.from(root.querySelectorAll("colgroup"))
            .map((group) => group.children[index])
            .filter(Boolean);

    // The body cells of this column, collected once. With `table-layout: auto`
    // a <col> width is only a hint, so the drag has to move the cells that
    // actually hold the column open — otherwise the body sits still under the
    // cursor and only jumps into place on release.
    // Two kinds of row are not part of the column and are skipped: the
    // zero-height measure row rc-table renders to measure real column widths
    // (pinning a width onto it would feed rc-table the drag's own answer back as
    // a measurement), and the empty-state row, whose single cell spans the whole
    // table and would otherwise be squeezed by a drag on the first column.
    const bodyCells =
      index < 0 || !root
        ? []
        : Array.from(root.querySelectorAll(".ant-table-tbody > tr"))
            .filter(
              (row) =>
                !row.classList.contains("ant-table-measure-row") &&
                !row.classList.contains("ant-table-placeholder")
            )
            .map((row) =>
              row.children.length > index ? row.children[index] : null
            )
            .filter(Boolean);

    /* The live preview is one injected stylesheet, not inline styles written
       onto the nodes.

       Every node the drag has to move is React's: the `<th>`, the body cells,
       and both `<colgroup>`s. The header's cols in particular are *measured* —
       rc-table renders the sticky header as its own `table-layout: fixed` table
       whose column widths come from a ResizeObserver on the body's measure row —
       so React re-renders them mid-drag with the very width being dragged to.
       Writing inline styles and clearing them on release therefore left the
       header's `<col>` blank for good: React's next render resolved to the width
       it had already rendered, so it wrote nothing back, and the header column
       collapsed to zero (or drifted out of line with its body cells) while the
       body kept the new width. That is the column "disappearing" on drop.

       A stylesheet keeps the drag entirely outside React's bookkeeping: the
       nodes carry a marker attribute React neither sets nor removes, the rule
       overrides whatever inline width React has written, and dropping is just
       "delete the rule" — which lands the DOM exactly where React thinks it
       already is, whatever it re-rendered while the mouse was down. */
    const dragging = [cell, ...bodyCells, ...cols];
    dragging.forEach((node) => node.setAttribute(DRAG_ATTR, ""));
    const preview = document.createElement("style");
    document.head.append(preview);

    // Spreadsheet feedback: a dashed line down the table at the edge being
    // dragged, and the width + delta in a badge beside it.
    const bounds = (root || cell).getBoundingClientRect();
    const guide = document.createElement("div");
    guide.className = "svResizeGuide";
    const badge = document.createElement("div");
    badge.className = "svResizeBadge";
    document.body.append(guide, badge);

    const paint = (clientX) => {
      const top = Math.max(bounds.top, 0);
      const height = Math.min(
        bounds.bottom - top,
        window.innerHeight - top
      );
      guide.style.top = `${top}px`;
      guide.style.height = `${Math.max(height, 0)}px`;
      guide.style.left = `${clientX}px`;

      const delta = Math.round(nextWidth - startWidth);
      badge.style.top = `${top + 6}px`;
      badge.style.left = `${clientX + 10}px`;
      badge.textContent = `${label} · ${nextWidth} px (${
        delta >= 0 ? "+" : "−"
      }${Math.abs(delta)})`;
    };

    // The width the drag is currently showing, as `!important` so it wins over
    // whatever inline width React has written on any of these nodes.
    const showWidth = (value) => {
      preview.textContent = `[${DRAG_ATTR}]{width:${value}px !important;min-width:${value}px !important;max-width:${value}px !important}`;
    };

    const onMove = (moveEvent) => {
      nextWidth = Math.max(
        MIN_COLUMN_WIDTH,
        Math.round(startWidth + moveEvent.clientX - startX)
      );
      showWidth(nextWidth);
      // The guide sits where the column edge will actually land, so it stops
      // following the cursor once the minimum width is reached.
      paint(startX + (nextWidth - startWidth));
    };

    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.classList.remove("svResizing");
      guide.remove();
      badge.remove();

      // Dropping the preview is all the DOM cleanup there is: every node goes
      // back to the width React last gave it, so the commit below is a normal
      // render against a DOM that matches what React believes about it.
      preview.remove();
      dragging.forEach((node) => node.removeAttribute(DRAG_ATTR));

      const settled = Math.round(nextWidth);
      const committed = width == null ? null : Math.round(width);
      if (Math.abs(nextWidth - startWidth) >= 2 && settled !== committed) {
        onResize(settled);
      }
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.classList.add("svResizing");
    paint(startX);
  };

  return (
    <th
      {...rest}
      className={`${className} svHead ${width == null ? "" : "svHead--sized"} ${
        folded ? "svHead--folded" : ""
      }`
        .replace(/\s+/g, " ")
        .trim()}
      style={
        width == null
          ? rest.style
          : { ...rest.style, width, minWidth: width, maxWidth: width }
      }
    >
      <span className="svHead__label">{children}</span>

      <Tooltip title={folded ? `Expand ${label}` : `Collapse ${label}`}>
        <button
          type="button"
          className="svHead__fold"
          onClick={(event) => {
            event.stopPropagation();
            onFold();
          }}
          aria-label={folded ? `Expand ${label}` : `Collapse ${label}`}
        >
          {/* Chevrons pointing at each other = fold; apart = unfold. */}
          <svg viewBox="0 0 12 12" width="10" height="10" aria-hidden="true">
            {folded ? (
              <path
                d="M4.5 2.5 L1.5 6 L4.5 9.5 M7.5 2.5 L10.5 6 L7.5 9.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            ) : (
              <path
                d="M1.5 2.5 L4.5 6 L1.5 9.5 M10.5 2.5 L7.5 6 L10.5 9.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            )}
          </svg>
        </button>
      </Tooltip>

      {!folded && resizable && (
        <span
          className="svHead__resizer"
          role="separator"
          aria-label={`Resize ${label}`}
          onMouseDown={startResize}
          onDoubleClick={(event) => {
            event.stopPropagation();
            onResetWidth();
          }}
          onClick={(event) => event.stopPropagation()}
        />
      )}
    </th>
  );
};

export default useColumnSizing;
